import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import Unsubscribe = firebase.Unsubscribe;
import {TaskModel} from "../../../tasks/models/task.model";
import {Service} from "../../models/service.model";
import {ServicesHttpService} from "../services/services-http.service";

export interface ServicesComponentState {
  services: Service[];
  cursor?: string;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  inRoute?: boolean;
}

@Injectable()
export class ServicesComponentStore extends ComponentStore<ServicesComponentState> {

  // Selectors
  readonly services$ = this.select((state) => state.services);
  readonly loading$ = this.select((state) => state.loading);
  readonly initialized$ = this.select((state) => state.initialized);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);


  vm$ = this.select({
    services: this.services$,
    loading: this.loading$,
    initialized: this.initialized$,
    error: this.error$,
    cursor: this.cursor$
  })

  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(ServicesHttpService) private servicesHttpService: ServicesHttpService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      services: [],
      cursor: '',
      loading: false,
      initialized: false,
      error: null,
      inRoute: undefined
    });
  }

  // Updaters
  readonly onSetServices = this.updater((state: ServicesComponentState, value: {
    services: Service[],
    cursor?: string
  }) => ({
    ...state,
    services: [...state.services, ...value.services],
    cursor: value.cursor,
    loading: false,
    initialized: true,
    error: null,
  }));

  readonly setInRoute = this.updater((state: ServicesComponentState, inRoute: boolean | undefined) => ({
    ...state,
    inRoute
  }));

  readonly updateService = this.updater((state: ServicesComponentState, updatedService: Service) => {
    if ((state.inRoute === false && !!updatedService.routeId) || (state.inRoute === true && !updatedService.routeId)) {
      const unsubscribe = this.channelsUnsubscribeMap.get(updatedService.id);
      if (unsubscribe) {
        unsubscribe();
        this.channelsUnsubscribeMap.delete(updatedService.id);
      }
      return {
        ...state,
        services: state.services.filter(s => s.id !== updatedService.id)
      };
    }

    return {
      ...state,
      services: state.services.map((service: Service) => {
        return updatedService.id === service.id ? {...service, ...updatedService} : service;
      }),
    };
  });

  // Updaters
  readonly onResetServices = this.updater((state: ServicesComponentState) => ({
    ...state,
    services: [],
    cursor: null,
    loading: false,
    initialized: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching services
  readonly getServices = this.effect(
    (trigger$: Observable<any>) =>
      trigger$.pipe(
        tap((params) => {
          this.setLoading(true);
          this.setInRoute(params.inRoute);
        }), // Set loading state and save inRoute filter
        withLatestFrom(this.cursor$), // Combine with cursor from the state
        switchMap(([params, cursor]) => {
            //TODO clean subscribed services
            return this.servicesHttpService.getServices({...params, cursor: cursor || ''}).pipe(
              tap({
                next: (response) => {

                  //Subscribe to services
                  response.list?.forEach((service: Service) => {
                    this.subscribe(service.id);
                  })
                  return this.onSetServices({services: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch services'),
              })
            )
          }
        )
      )
  );

  readonly resetServices = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetServices();
      })
    )
  );

  public unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'services_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const service: Service = snapshot.val();
        if (!!service) {
          this.updateService(service);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }

}
